import type {
  Command,
  CommandReceipt,
  CommandResult,
  Room,
  PlatformRoom,
  Result,
  ErrorInfo,
} from "../shared/model.ts";
import { canonical } from "../shared/canonical.ts";
import validateServerFrame from "./wire-validator.js";
export type Viewport = {
  width: number;
  height: number;
  pixelRatio: number;
  safeInsets: { top: number; right: number; bottom: number; left: number };
};
export type UiConfig = {
  soundEnabled: boolean;
  musicEnabled: boolean;
  hapticsEnabled: boolean;
  hostControls: string[];
};
export type SdkEvent = {
  schemaVersion: 1;
  name: string;
  instanceId: string;
  occurredAtMs: number;
  payload: any;
};
export type SdkConfig = {
  appId: string;
  environment: "test" | "production";
  apiBaseUrl: string;
  manifestPublicKey: string;
  logLevel?: "error" | "warn" | "info" | "debug";
};
export type LoadOptions = {
  gameId: string;
  userId: string;
  displayName?: string;
  hostRoomId?: string;
  locale: string;
  viewport: Viewport;
  ui: UiConfig;
  getCode: (
    reason: "initial" | "renew" | "resume",
  ) => Promise<{ code: string }>;
  onEvent: (event: SdkEvent) => void;
  runtime?: GameRuntime;
};
export type SessionState =
  | "IDLE"
  | "LOADING"
  | "AUTHENTICATING"
  | "CONNECTING"
  | "READY"
  | "RECONNECTING"
  | "FAILED"
  | "DESTROYING"
  | "DESTROYED";
export interface GameContext {
  instanceId: string;
  gameVersion: string;
  gameId: string;
  capabilities: readonly string[];
  hostControls: readonly string[];
  signal: AbortSignal;
  userId: string;
  send: (command: Command) => Promise<CommandReceipt>;
  perform: (command: Command) => Promise<CommandResult>;
  history: () => Promise<any[]>;
  updateUi: (ui: Partial<UiConfig>) => Promise<Result<void>>;
  requestSync: () => void;
  getServerOffset: () => number;
  sendEphemeral: (message: {
    name: string;
    roomId: string;
    roundId: string;
    payload: Record<string, unknown>;
  }) => void;
  requestHostAction: (
    action: string,
    context: unknown,
  ) => Promise<Result<unknown>>;
  requestAction: (
    action: string,
    context?: Record<string, unknown>,
  ) => Promise<Result<unknown>>;
  emitLocal: (name: string, payload: unknown) => void;
}
export interface GameRuntime {
  mount: (context: GameContext, view: HTMLElement) => Promise<void>;
  onSnapshot: (room: PlatformRoom | null) => void;
  onEvent: (event: SdkEvent) => void;
  resize: (viewport: Viewport) => void;
  setPresentation: (options: { foreground: boolean; ui: UiConfig }) => void;
  unmount: () => Promise<void>;
}
const fault = (code: string, message: string): ErrorInfo => ({
  code,
  message,
  retryable: ["NETWORK_TIMEOUT", "RESULT_UNKNOWN"].includes(code),
});
const ok = <T>(data: T): Result<T> => ({ ok: true, data });
export class GameSession {
  readonly instanceId = crypto.randomUUID();
  readonly view = document.createElement("div");
  state: SessionState = "IDLE";
  room: PlatformRoom | null = null;
  serverOffset = 0;
  #config: SdkConfig;
  #options: LoadOptions;
  #token = "";
  #sessionId = "";
  #expires = 0;
  #socket: WebSocket | null = null;
  #destroyed = false;
  #controller = new AbortController();
  #renewing: Promise<void> | null = null;
  #timers = new Set<ReturnType<typeof setTimeout>>();
  #heartbeat: ReturnType<typeof setInterval> | null = null;
  #lastMessage = 0;
  #epoch = "";
  #seq = 0;
  #retry = 0;
  #receipts = new Map<
    string,
    {
      resolve: (r: CommandReceipt) => void;
      reject: (e: unknown) => void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();
  #actions = new Map<
    string,
    {
      resolve: (r: Result<unknown>) => void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();
  #resources = new Map<string, Blob>();
  #objectUrls: string[] = [];
  #surface = this.view.attachShadow({ mode: "open" });
  #mounted = false;
  #verifiedManifest: any = null;
  #gameValidator: ((frame: unknown) => boolean) | null = null;
  #roundEvents = new Set<string>();
  #performing = new Map<
    string,
    { digest: string; promise: Promise<CommandResult> }
  >();
  #sending = new Map<
    string,
    { digest: string; promise: Promise<CommandReceipt> }
  >();
  #gameCommands = new Map<
    string,
    { digest: string; promise: Promise<CommandResult> }
  >();
  #destroyPromise: Promise<Result<void>> | null = null;
  #syncWaiters = new Set<() => void>();
  constructor(config: SdkConfig, options: LoadOptions) {
    this.#config = config;
    this.#options = {
      ...options,
      ui: { ...options.ui, hostControls: [...options.ui.hostControls] },
    };
    this.view.className = "eightball-sdk-container";
  }
  guarded<T>(work: Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      const signal = this.#controller.signal;
      const cleanup = () => {
        clearTimeout(timeout);
        signal.removeEventListener("abort", abort);
      };
      const abort = () => {
        cleanup();
        reject(fault("LOAD_CANCELLED", "加载已取消"));
      };
      const timeout = setTimeout(() => {
        cleanup();
        reject(fault("NETWORK_TIMEOUT", "宿主响应超时"));
      }, 10000);
      if (signal.aborted) {
        abort();
        return;
      }
      signal.addEventListener("abort", abort, { once: true });
      work.then(
        (value) => {
          cleanup();
          resolve(value);
        },
        (error) => {
          cleanup();
          reject(error);
        },
      );
    });
  }
  event(name: string, payload: any) {
    if (this.#destroyed) return;
    if (name === "round.started" || name === "round.settled") {
      const key = payload?.roundId + ":" + name;
      if (!payload?.roundId || this.#roundEvents.has(key)) return;
      this.#roundEvents.add(key);
    }
    const e: SdkEvent = {
      schemaVersion: 1,
      name,
      instanceId: this.instanceId,
      occurredAtMs: Date.now(),
      payload,
    };
    try {
      this.#options.onEvent(e);
    } catch {}
    try {
      if (this.#mounted) this.#options.runtime?.onEvent(e);
    } catch {}
  }
  later(fn: () => void, delay: number) {
    const t = setTimeout(() => {
      this.#timers.delete(t);
      if (!this.#destroyed) fn();
    }, delay);
    this.#timers.add(t);
    return t;
  }
  async http<T>(path: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(new URL(path, this.#config.apiBaseUrl), {
      ...options,
      signal: AbortSignal.any([
        this.#controller.signal,
        AbortSignal.timeout(10000),
      ]),
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(this.#token ? { Authorization: `Bearer ${this.#token}` } : {}),
        ...options.headers,
      },
    });
    const result = (await response.json()) as Result<T>;
    if (!result.ok) throw result.error;
    return result.data;
  }
  async authenticate(reason: "initial" | "renew" | "resume") {
    if (this.#renewing) return this.#renewing;
    this.#renewing = (async () => {
      const { code } = await this.guarded(this.#options.getCode(reason));
      if (this.#destroyed) return;
      const renew = reason === "renew" && Date.now() < this.#expires;
      const data = await this.http<{
        sessionId: string;
        sessionToken: string;
        expiresAtMs: number;
      }>(renew ? "/v1/auth/renew" : "/v1/auth/exchange", {
        method: "POST",
        body: JSON.stringify({
          code,
          requestId: crypto.randomUUID(),
          instanceId: this.instanceId,
          sdkVersion: "0.2.0",
          appId: this.#config.appId,
          gameId: this.#options.gameId,
          hostRoomId: this.#options.hostRoomId,
          sessionId: renew ? this.#sessionId : undefined,
        }),
      });
      this.#token = data.sessionToken;
      this.#sessionId = data.sessionId;
      this.#expires = data.expiresAtMs;
      if (this.#socket?.readyState === WebSocket.OPEN)
        this.#socket.send(
          JSON.stringify({
            type: "auth",
            token: this.#token,
            instanceId: this.instanceId,
          }),
        );
      this.later(
        () =>
          void this.authenticate("renew").catch(() => {
            this.event("auth.required", { reason: "renew" });
            this.#socket?.close();
          }),
        Math.max(1000, this.#expires - Date.now() - 60000),
      );
    })().finally(() => (this.#renewing = null));
    return this.#renewing;
  }
  async verifyPackage(candidate?: any, cachedOnly = false): Promise<void> {
    const manifest =
        candidate ??
        (await this.http<any>(`/v1/games/${this.#options.gameId}/manifest`)),
      { signature, ...content } = manifest;
    const pem = this.#config.manifestPublicKey.replace(
        /-----[^-]+-----|\s/g,
        "",
      ),
      keyBytes = Uint8Array.from(atob(pem), (c) => c.charCodeAt(0));
    const key = await crypto.subtle.importKey(
      "spki",
      keyBytes,
      { name: "Ed25519" },
      false,
      ["verify"],
    );
    const valid = await crypto.subtle.verify(
      "Ed25519",
      key,
      Uint8Array.from(atob(signature), (c) => c.charCodeAt(0)),
      new TextEncoder().encode(canonical(content)),
    );
    if (!valid) throw fault("PACKAGE_INVALID", "游戏资源清单校验失败");
    if (
      content.gameId !== this.#options.gameId ||
      content.protocolVersion !== 1 ||
      !/^\d+\.\d+\.\d+$/.test(content.minSdkVersion) ||
      content.minSdkVersion
        .split(".")
        .map(Number)
        .some((v: number, i: number, all: number[]) => {
          const current = [0, 2, 0];
          return (
            all.slice(0, i).every((n, j) => n === current[j]) && v > current[i]
          );
        }) ||
      !Array.isArray(content.capabilities)
    )
      throw fault("VERSION_UNSUPPORTED", "游戏版本不兼容");
    const base = new URL(this.#config.apiBaseUrl),
      cache =
        "caches" in globalThis
          ? await caches.open(
              `eightball-${content.gameVersion}-${signature.slice(0, 20)}`,
            )
          : null;
    try {
      for (let i = 0; i < content.assets.length; i++) {
        const asset = content.assets[i],
          url = new URL(asset.path, base);
        if (url.origin !== base.origin || asset.path.includes(".."))
          throw fault("PACKAGE_INVALID", "资源地址不合法");
        let response = await cache?.match(url.href);
        if (!response && cachedOnly)
          throw fault("RESOURCE_DOWNLOAD_FAILED", "历史资源缓存不完整");
        if (!response)
          response = await fetch(url, {
            signal: AbortSignal.any([
              this.#controller.signal,
              AbortSignal.timeout(10000),
            ]),
            redirect: "error",
          });
        if (!response.ok)
          throw fault("RESOURCE_DOWNLOAD_FAILED", "资源下载失败");
        const bytes = await response.clone().arrayBuffer(),
          digest = Array.from(
            new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)),
            (x) => x.toString(16).padStart(2, "0"),
          ).join("");
        if (digest !== asset.sha256 || bytes.byteLength !== asset.size) {
          await cache?.delete(url.href);
          throw fault("PACKAGE_INVALID", "游戏文件损坏");
        }
        this.#resources.set(
          asset.path,
          new Blob([bytes], {
            type: asset.path.endsWith(".js")
              ? "text/javascript"
              : asset.path.endsWith(".css")
                ? "text/css"
                : "application/octet-stream",
          }),
        );
        await cache?.put(url.href, response);
        this.event("loading.progress", {
          stage: "resources",
          progress: (i + 1) / content.assets.length,
        });
      }
    } catch (error) {
      if (
        !cachedOnly &&
        !this.#destroyed &&
        "caches" in globalThis &&
        (error as ErrorInfo).code !== "PACKAGE_INVALID"
      ) {
        const history = await caches.open("eightball-verified-manifests"),
          entry = await history.match(
            new URL(
              "/__eightball_good/" +
                encodeURIComponent(this.#config.appId) +
                "/" +
                this.#options.gameId,
              base,
            ),
          );
        if (entry) {
          const previous = await entry.json();
          if (
            previous.signature !== manifest.signature &&
            (
              manifest.compatibleGameVersions ?? [manifest.gameVersion]
            ).includes(previous.gameVersion)
          ) {
            this.#resources.clear();
            this.event("loading.fallback", {
              from: manifest.gameVersion,
              to: previous.gameVersion,
              reason: "RESOURCE_DOWNLOAD_FAILED",
            });
            return this.verifyPackage(previous, true);
          }
        }
      }
      throw error;
    }
    if (this.#destroyed) throw fault("LOAD_CANCELLED", "加载已取消");
    this.#verifiedManifest = manifest;
    if (!content.protocolValidator)
      throw fault("VERSION_UNSUPPORTED", "缺少游戏协议校验器");
    const protocol = this.#resources.get(content.protocolValidator);
    if (!protocol) throw fault("PACKAGE_INVALID", "游戏协议校验器缺失");
    const protocolUrl = URL.createObjectURL(protocol);
    this.#objectUrls.push(protocolUrl);
    const protocolModule = await import(/* @vite-ignore */ protocolUrl);
    if (this.#destroyed) throw fault("LOAD_CANCELLED", "加载已取消");
    if (typeof protocolModule.default !== "function")
      throw fault("PACKAGE_INVALID", "游戏协议校验器无效");
    this.#gameValidator = protocolModule.default;
    if (!this.#options.runtime) {
      const source = this.#resources.get(content.runtimeEntry);
      if (!source) throw fault("PACKAGE_INVALID", "游戏入口缺失");
      for (const path of content.styles ?? []) {
        const css = this.#resources.get(path);
        if (!css) throw fault("PACKAGE_INVALID", "游戏样式缺失");
        const style = document.createElement("style");
        style.textContent = await css.text();
        this.#surface.append(style);
      }
      const url = URL.createObjectURL(source);
      this.#objectUrls.push(url);
      const module = await import(/* @vite-ignore */ url);
      if (this.#destroyed) throw fault("LOAD_CANCELLED", "加载已取消");
      this.#options.runtime = module.createRuntime({
        name: this.#options.displayName,
      });
    }
  }
  async load() {
    try {
      this.state = "LOADING";
      this.event("loading.progress", { stage: "authentication", progress: 0 });
      this.state = "AUTHENTICATING";
      await this.authenticate("initial");
      await this.verifyPackage();
      if (this.#destroyed) throw fault("LOAD_CANCELLED", "加载已取消");
      const gameRoot = document.createElement("div");
      gameRoot.className = "eightball-game-view";
      this.#surface.append(gameRoot);
      this.#mounted = true;
      await this.guarded(
        this.#options.runtime?.mount(
          Object.freeze({
            instanceId: this.instanceId,
            gameVersion: this.#verifiedManifest.gameVersion,
            gameId: this.#options.gameId,
            capabilities: Object.freeze([
              ...this.#verifiedManifest.capabilities,
            ]),
            hostControls: Object.freeze([...this.#options.ui.hostControls]),
            signal: this.#controller.signal,
            userId: this.#options.userId,
            send: (c: Command) => this.sendCommand(c),
            perform: (c: Command) => this.performFromGame(c),
            history: () => this.history(),
            updateUi: (u: Partial<UiConfig>) => this.updateUi(u),
            requestSync: () => this.requestSync(),
            getServerOffset: () => this.serverOffset,
            sendEphemeral: (m: any) => this.sendEphemeral(m),
            requestHostAction: (a: string, c: unknown) =>
              this.requestHostAction(a, c),
            requestAction: (a: string, c?: Record<string, unknown>) =>
              this.requestAction(a, c),
            emitLocal: (n: string, p: unknown) => {
              if (!n.startsWith("round.") && !n.startsWith("command."))
                this.event(n, p);
            },
          }),
          gameRoot,
        ) ?? Promise.resolve(),
      );
      if (this.#destroyed) throw fault("LOAD_CANCELLED", "加载已取消");
      await this.connect();
      this.#options.runtime?.resize(this.#options.viewport);
      this.#options.runtime?.onSnapshot(this.room);
      this.#options.runtime?.setPresentation({
        foreground: !document.hidden,
        ui: this.#options.ui,
      });
      if ("caches" in globalThis && this.#verifiedManifest) {
        try {
          const history = await caches.open("eightball-verified-manifests");
          await history.put(
            new URL(
              "/__eightball_good/" +
                encodeURIComponent(this.#config.appId) +
                "/" +
                this.#options.gameId,
              this.#config.apiBaseUrl,
            ),
            new Response(JSON.stringify(this.#verifiedManifest), {
              headers: { "Content-Type": "application/json" },
            }),
          );
        } catch {}
      }
      this.event("session.ready", {
        gameVersion: this.#verifiedManifest.gameVersion,
      });
      return this;
    } catch (error) {
      await this.destroy();
      throw error;
    }
  }
  connect(): Promise<void> {
    this.state = this.#retry ? "RECONNECTING" : "CONNECTING";
    return new Promise((resolve, reject) => {
      const url = new URL("/v1/game-stream", this.#config.apiBaseUrl);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      const socket = new WebSocket(url);
      this.#socket = socket;
      let authenticated = false;
      let initialSnapshot = false;
      const timeout = this.later(() => {
        socket.close();
        reject(fault("NETWORK_TIMEOUT", "游戏连接超时"));
      }, 7000);
      socket.onopen = () =>
        socket.send(
          JSON.stringify({
            type: "auth",
            token: this.#token,
            instanceId: this.instanceId,
          }),
        );
      socket.onmessage = (event) => {
        if (this.#socket !== socket || this.#destroyed) return;
        this.#lastMessage = Date.now();
        let m: any;
        try {
          m = JSON.parse(event.data);
        } catch {
          return;
        }
        if (!validateServerFrame(m) || !this.#gameValidator?.(m)) {
          this.event(
            "game.error",
            fault("PROTOCOL_INVALID", "服务消息格式不正确"),
          );
          socket.close(4002, "invalid protocol");
          return;
        }
        if (m.instanceId !== this.instanceId) return;
        this.serverOffset = m.serverTimeMs - Date.now();
        if (m.type === "auth") {
          if (
            m.identity.userId !== this.#options.userId ||
            m.identity.appId !== this.#config.appId ||
            m.identity.gameId !== this.#options.gameId ||
            (m.identity.hostRoomId ?? null) !==
              (this.#options.hostRoomId ?? null)
          ) {
            socket.close(4001, "identity mismatch");
            reject(fault("AUTH_INVALID", "宿主与游戏身份不一致"));
            return;
          }
          authenticated = true;
          this.state = "READY";
          this.#retry = 0;
          this.event("connection.changed", { state: "READY" });
          if (this.#heartbeat) clearInterval(this.#heartbeat);
          this.#heartbeat = setInterval(() => {
            if (this.#destroyed) return;
            if (Date.now() - this.#lastMessage > 35000) socket.close();
            else if (socket.readyState === WebSocket.OPEN)
              socket.send(JSON.stringify({ type: "ping" }));
          }, 10000);
          return;
        }
        if (m.type === "snapshot") {
          this.#epoch = m.streamEpoch;
          this.#seq = m.lastServerSeq;
          this.room = m.room;
          if (this.room?.round) {
            this.event("round.started", {
              roundId: this.room.round.roundId,
              roomId: this.room.roomId,
              gameId: this.room.gameId,
            });
            if (this.room.round.result)
              this.event("round.settled", this.room.round.result);
          }
          if (this.#mounted) this.#options.runtime?.onSnapshot(this.room);
          this.event("room.updated", { room: this.room });
          for (const done of this.#syncWaiters) done();
          this.#syncWaiters.clear();
          if (authenticated && !initialSnapshot) {
            initialSnapshot = true;
            clearTimeout(timeout);
            this.#timers.delete(timeout);
            resolve();
          }
          return;
        }
        if (m.type === "receipt") {
          const waiter = this.#receipts.get(m.requestId);
          if (waiter) {
            clearTimeout(waiter.timer);
            this.#timers.delete(waiter.timer);
            this.#receipts.delete(m.requestId);
            waiter.resolve(m);
          }
          return;
        }
        if (m.type === "event") {
          if (m.streamEpoch !== this.#epoch || m.serverSeq > this.#seq + 1) {
            this.requestSync();
            return;
          }
          if (m.serverSeq <= this.#seq) return;
          this.#seq = m.serverSeq;
          this.event(m.name, m.payload);
          return;
        }
        if (m.type === "ephemeral") {
          this.event(m.name, m.payload);
          return;
        }
        if (m.type === "error") this.event("game.error", m.error);
      };
      socket.onclose = (e) => {
        clearTimeout(timeout);
        this.#timers.delete(timeout);
        if (this.#socket !== socket || this.#destroyed) {
          if (!initialSnapshot) reject(fault("LOAD_CANCELLED", "加载已取消"));
          return;
        }
        if (this.#heartbeat) {
          clearInterval(this.#heartbeat);
          this.#heartbeat = null;
        }
        if (!initialSnapshot) reject(fault("NETWORK_TIMEOUT", "连接中断"));
        if (e.code === 4009) {
          this.state = "FAILED";
          this.event(
            "game.error",
            fault("CONTROL_TRANSFERRED", "其他设备已接管本局"),
          );
          return;
        }
        this.state = "RECONNECTING";
        const delay =
          Math.min(10000, 1000 * 2 ** Math.min(this.#retry++, 4)) +
          Math.random() * 300;
        this.event("connection.changed", {
          state: "RECONNECTING",
          retryAtMs: Date.now() + delay,
        });
        this.later(() => this.resumeConnection(), delay);
      };
      socket.onerror = () => {};
    });
  }
  resumeConnection() {
    if (this.#destroyed) return;
    void (
      Date.now() >= this.#expires - 5000
        ? this.authenticate("resume")
        : Promise.resolve()
    )
      .then(() => {
        if (!this.#destroyed) return this.connect();
      })
      .catch(() => {
        if (!this.#destroyed && this.#socket?.readyState === WebSocket.CLOSED)
          this.later(() => this.resumeConnection(), 5000);
      });
  }
  getView() {
    return this.view;
  }
  getState() {
    return this.state;
  }
  sendCommand(command: Command): Promise<CommandReceipt> {
    const digest = canonical(command),
      prior = this.#sending.get(command.requestId);
    if (prior)
      return prior.digest === digest
        ? prior.promise
        : Promise.resolve({
            requestId: command.requestId,
            status: "REJECTED",
            error: fault("IDEMPOTENCY_CONFLICT", "请求编号冲突"),
          });
    const promise = this.sendOnce(command).finally(() =>
      this.#sending.delete(command.requestId),
    );
    this.#sending.set(command.requestId, { digest, promise });
    return promise;
  }
  sendOnce(command: Command): Promise<CommandReceipt> {
    if (this.state !== "READY" || this.#socket?.readyState !== WebSocket.OPEN)
      return Promise.resolve({
        requestId: command.requestId,
        status: "REJECTED",
        error: fault("SDK_NOT_READY", "正在恢复连接，请稍候"),
      });
    return new Promise((resolve, reject) => {
      const timer = this.later(() => {
        this.#receipts.delete(command.requestId);
        reject({
          ...fault("RESULT_UNKNOWN", "请求结果未知，请查询原请求"),
          requestId: command.requestId,
        });
      }, 7000);
      this.#receipts.set(command.requestId, { resolve, reject, timer });
      this.#socket!.send(JSON.stringify({ type: "command", command }));
    });
  }
  async getCommandResult(requestId: string): Promise<Result<CommandResult>> {
    try {
      return ok(
        await this.http<CommandResult>(
          "/v1/commands/" + encodeURIComponent(requestId),
        ),
      );
    } catch (e) {
      return { ok: false, error: e as ErrorInfo };
    }
  }
  perform(command: Command): Promise<CommandResult> {
    const digest = canonical(command),
      prior = this.#performing.get(command.requestId);
    if (prior)
      return prior.digest === digest
        ? prior.promise
        : Promise.reject(fault("IDEMPOTENCY_CONFLICT", "请求编号冲突"));
    const promise = this.performOnce(command).finally(() =>
      this.#performing.delete(command.requestId),
    );
    this.#performing.set(command.requestId, { digest, promise });
    return promise;
  }
  async performOnce(command: Command): Promise<CommandResult> {
    try {
      const receipt = await this.sendCommand(command);
      if (receipt.status === "REJECTED") throw receipt.error;
    } catch (e) {
      if ((e as ErrorInfo).code !== "RESULT_UNKNOWN") throw e;
    }
    const deadline = Date.now() + 45000;
    while (!this.#destroyed && Date.now() < deadline) {
      const result = await this.getCommandResult(command.requestId);
      if (result.ok && result.data.status === "SUCCEEDED") return result.data;
      if (result.ok && result.data.status === "FAILED") throw result.data.error;
      await this.guarded(new Promise((resolve) => setTimeout(resolve, 150)));
    }
    throw {
      ...fault("RESULT_UNKNOWN", "操作结果尚未确认，请恢复连接查询"),
      requestId: command.requestId,
    };
  }
  async updateCode(code: string): Promise<Result<void>> {
    try {
      const d = await this.http<any>("/v1/auth/renew", {
        method: "POST",
        body: JSON.stringify({
          code,
          sessionId: this.#sessionId,
          requestId: crypto.randomUUID(),
        }),
      });
      this.#token = d.sessionToken;
      this.#expires = d.expiresAtMs;
      this.#socket?.send(
        JSON.stringify({
          type: "auth",
          token: this.#token,
          instanceId: this.instanceId,
        }),
      );
      return ok(undefined);
    } catch (e) {
      return { ok: false, error: e as ErrorInfo };
    }
  }
  sendEphemeral(message: {
    name: string;
    roomId: string;
    roundId: string;
    payload: Record<string, unknown>;
  }) {
    if (
      message.name.startsWith(this.#options.gameId + ".") &&
      this.state === "READY" &&
      this.#socket?.readyState === WebSocket.OPEN
    )
      this.#socket.send(JSON.stringify({ type: "ephemeral", ...message }));
  }
  requestSync() {
    if (this.#socket?.readyState === WebSocket.OPEN)
      this.#socket.send(JSON.stringify({ type: "sync" }));
  }
  async updateViewport(v: Viewport) {
    if (this.#destroyed)
      return {
        ok: false as const,
        error: fault("SESSION_DESTROYED", "会话已销毁"),
      };
    this.#options.viewport = v;
    this.#options.runtime?.resize(v);
    return ok(undefined);
  }
  async updateUi(ui: Partial<UiConfig>) {
    this.#options.ui = { ...this.#options.ui, ...ui };
    if (this.#destroyed)
      return {
        ok: false as const,
        error: fault("SESSION_DESTROYED", "会话已销毁"),
      };
    this.#options.runtime?.setPresentation({
      foreground: !document.hidden,
      ui: this.#options.ui,
    });
    return ok(undefined);
  }
  async setForeground(foreground: boolean) {
    if (this.#destroyed)
      return {
        ok: false as const,
        error: fault("SESSION_DESTROYED", "会话已销毁"),
      };
    this.#options.runtime?.setPresentation({
      foreground,
      ui: this.#options.ui,
    });
    if (foreground) {
      let done: (() => void) | undefined;
      try {
        await this.guarded(
          new Promise<void>((resolve) => {
            done = resolve;
            this.#syncWaiters.add(resolve);
            this.requestSync();
          }),
        );
      } catch (error) {
        return { ok: false as const, error: error as ErrorInfo };
      } finally {
        if (done) this.#syncWaiters.delete(done);
      }
    }
    return ok(undefined);
  }
  requestHostAction(
    action: string,
    context: unknown,
  ): Promise<Result<unknown>> {
    if (this.#destroyed)
      return Promise.resolve({
        ok: false,
        error: fault("SESSION_DESTROYED", "会话已销毁"),
      });
    return new Promise((resolve) => {
      const actionId = crypto.randomUUID(),
        timer = this.later(
          () => void this.resolveHostAction(actionId, "DENY"),
          10000,
        );
      this.#actions.set(actionId, { resolve, timer });
      this.event("host.actionRequested", {
        actionId,
        action,
        context,
        deadlineAtMs: Date.now() + 10000,
      });
    });
  }
  async resolveHostAction(
    actionId: string,
    outcome: "ALLOW" | "DENY",
    data?: unknown,
  ): Promise<Result<void>> {
    if (outcome !== "ALLOW" && outcome !== "DENY")
      return {
        ok: false,
        error: fault("INVALID_ARGUMENT", "只接受 ALLOW/DENY"),
      };
    const action = this.#actions.get(actionId);
    if (!action)
      return { ok: false, error: fault("ACTION_EXPIRED", "请求已结束") };
    clearTimeout(action.timer);
    this.#timers.delete(action.timer);
    this.#actions.delete(actionId);
    action.resolve(
      outcome === "ALLOW"
        ? ok(undefined)
        : { ok: false, error: fault("ACTION_DENIED", "操作取消") },
    );
    return ok(undefined);
  }
  async requestAction(
    action: string,
    context: Record<string, unknown> = {},
  ): Promise<Result<unknown>> {
    if (action === "share") action = "invite";
    if (!this.#options.ui.hostControls.includes(action)) return ok(undefined);
    return this.requestHostAction(action, {
      roomId: this.room?.roomId,
      roundId: this.room?.round?.roundId,
      ...context,
    });
  }
  performFromGame(command: Command): Promise<CommandResult> {
    const digest = canonical(command),
      prior = this.#gameCommands.get(command.requestId);
    if (prior)
      return prior.digest === digest
        ? prior.promise
        : Promise.reject(fault("IDEMPOTENCY_CONFLICT", "请求编号冲突"));
    const promise = this.performFromGameOnce(command).finally(() =>
      this.#gameCommands.delete(command.requestId),
    );
    this.#gameCommands.set(command.requestId, { digest, promise });
    return promise;
  }
  async performFromGameOnce(command: Command): Promise<CommandResult> {
    const action = (
      {
        "room.match": "join",
        "room.create": "join",
        "room.join": "join",
        "room.leave": "exit",
        "room.ready": "ready",
        "room.start": "start",
        "room.invite": "invite",
        "room.rematch": "rematch",
      } as Record<string, string>
    )[command.name];
    if (action) {
      const decision = await this.requestAction(action, {
        requestId: command.requestId,
        roomId: command.roomId ?? command.payload.roomId ?? this.room?.roomId,
        roundId: command.roundId ?? this.room?.round?.roundId,
        command: command.name,
        parameters: command.payload,
      });
      if (!decision.ok) throw decision.error;
    }
    return this.perform(command);
  }
  async history() {
    return this.http<any[]>("/v1/me/history");
  }
  destroy(): Promise<Result<void>> {
    return (this.#destroyPromise ??= this.destroyOnce());
  }
  async destroyOnce(): Promise<Result<void>> {
    if (this.#destroyed) return ok(undefined);
    this.state = "DESTROYING";
    this.#destroyed = true;
    this.#controller.abort();
    for (const t of this.#timers) clearTimeout(t);
    this.#timers.clear();
    if (this.#heartbeat) clearInterval(this.#heartbeat);
    for (const p of this.#receipts.values())
      p.reject(fault("RESULT_UNKNOWN", "会话已关闭"));
    this.#receipts.clear();
    for (const p of this.#actions.values())
      p.resolve({ ok: false, error: fault("ACTION_DENIED", "会话已关闭") });
    this.#actions.clear();
    this.#socket?.close(1000, "destroy");
    try {
      if (this.#mounted) await this.#options.runtime?.unmount();
    } catch {}
    this.#mounted = false;
    this.view.remove();
    this.#surface.replaceChildren();
    for (const url of this.#objectUrls) URL.revokeObjectURL(url);
    this.#objectUrls = [];
    this.#resources.clear();
    this.state = "DESTROYED";
    try {
      this.#options.onEvent({
        schemaVersion: 1,
        name: "session.destroyed",
        instanceId: this.instanceId,
        occurredAtMs: Date.now(),
        payload: { reason: "destroy" },
      });
    } catch {}
    return ok(undefined);
  }
}
export class GameSdk {
  #config: SdkConfig | null = null;
  #session: GameSession | null = null;
  #loading = false;
  #generation = 0;
  async init(config: SdkConfig): Promise<Result<{ sdkVersion: string }>> {
    if (this.#session && canonical(config) !== canonical(this.#config))
      return { ok: false, error: fault("SDK_BUSY", "请先释放当前游戏") };
    if (!config.appId || !config.manifestPublicKey)
      return { ok: false, error: fault("INVALID_ARGUMENT", "SDK 配置不完整") };
    this.#config = {
      ...config,
      apiBaseUrl: new URL(config.apiBaseUrl, location.href).href,
    };
    return ok({ sdkVersion: this.getVersion() });
  }
  getVersion() {
    return "0.2.0";
  }
  async getGameCatalog(): Promise<Result<any[]>> {
    if (!this.#config)
      return { ok: false, error: fault("SDK_NOT_READY", "请先初始化") };
    return fetch(new URL("/v1/games", this.#config.apiBaseUrl)).then((r) =>
      r.json(),
    );
  }
  async loadGame(options: LoadOptions): Promise<Result<GameSession>> {
    if (!this.#config)
      return { ok: false, error: fault("SDK_NOT_READY", "请先初始化") };
    if (this.#loading || (this.#session && this.#session.state !== "DESTROYED"))
      return { ok: false, error: fault("SDK_BUSY", "已有活动会话") };
    this.#loading = true;
    const generation = this.#generation;
    try {
      this.#session = new GameSession(this.#config, options);
      const session = this.#session;
      await session.load();
      if (generation !== this.#generation)
        throw fault("LOAD_CANCELLED", "加载已取消");
      return ok(session);
    } catch (e) {
      return {
        ok: false,
        error: (e as ErrorInfo)?.code
          ? (e as ErrorInfo)
          : fault("LOAD_FAILED", "加载失败，请检查网络重试"),
      };
    } finally {
      if (generation === this.#generation) this.#loading = false;
    }
  }
  async uninit() {
    const session = this.#session;
    this.#generation++;
    this.#session = null;
    this.#config = null;
    this.#loading = false;
    await session?.destroy();
    return ok(undefined);
  }
}
export type { Command, CommandResult, CommandReceipt, Result, Room };
